/**
 * optimizers.js
 *
 * Algoritmi di ottimizzazione layout per GridView.
 * Funzioni pure — zero dipendenze React, testabili isolatamente.
 *
 * Ogni funzione legge i propri parametri da config.meta.optimizations.
 * Se la sezione optimizations manca dal config, le funzioni ritornano array vuoti.
 *
 * API:
 *   optimizeImageSizes(placements, config) → Map<index, suggestion>
 *   fillEmptySpace(pages, placements, config) → Map<index, suggestion>
 */

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Replica di getField da LayoutPreview — risolve il nome campo tramite csvMapping.
 */
function getField(product, fieldName, csvMapping) {
  if (!product) return '';
  if (product[fieldName] !== undefined) return product[fieldName] ?? '';
  const mapped = csvMapping?.[fieldName];
  if (mapped && product[mapped] !== undefined) return product[mapped] ?? '';
  const lower = fieldName.toLowerCase();
  const key = Object.keys(product).find(k => k.toLowerCase() === lower);
  return key ? (product[key] ?? '') : '';
}

/**
 * Conta i caratteri equivalenti trattando ogni <br> come un a-capo che
 * "completa" la riga corrente fino a avgCh. Così un segmento corto dopo
 * un <br> occupa una riga intera nella stima. Il risultato è confrontabile
 * con avgCh × maxRows (stessa unità del config).
 */
function charCountWithBreaks(product, textFields, csvMapping, avgCh) {
  let total = 0;
  for (const fieldName of textFields) {
    const val = String(getField(product, fieldName, csvMapping));
    const segments = val.split(/<br\s*\/?>/gi);
    for (let s = 0; s < segments.length; s++) {
      const clean = segments[s].replace(/<[^>]+>/g, '').trim();
      total += clean.length;
      if (s < segments.length - 1) {
        const remainder = total % avgCh;
        if (remainder > 0) total += avgCh - remainder;
      }
    }
  }
  return total;
}

/**
 * Trova il layout nel config per id.
 */
function findLayout(config, layoutId) {
  return config?.layouts?.find(l => l.id === layoutId);
}

// ─── Packing (replica leggera di GridViewBuilder) ────────────────────────────

const GRID_COLS = 6;
const GRID_ROWS = 12;

function newGrid(cols, rows) {
  return Array.from({ length: rows }, () => new Array(cols).fill(false));
}

function findFit(grid, colSpan, rowSpan, cols, rows, minRow = 0) {
  for (let row = minRow; row <= rows - rowSpan; row++) {
    for (let col = 0; col <= cols - colSpan; col++) {
      let fits = true;
      outer: for (let r = row; r < row + rowSpan; r++) {
        for (let c = col; c < col + colSpan; c++) {
          if (grid[r][c]) { fits = false; break outer; }
        }
      }
      if (fits) return { col, row };
    }
  }
  return null;
}

function markOccupied(grid, col, row, colSpan, rowSpan) {
  for (let r = row; r < row + rowSpan; r++) {
    for (let c = col; c < col + colSpan; c++) {
      grid[r][c] = true;
    }
  }
}

/**
 * Conta le celle occupate in una griglia.
 */
function countOccupied(grid) {
  let count = 0;
  for (const row of grid) {
    for (const cell of row) {
      if (cell) count++;
    }
  }
  return count;
}

/**
 * Esegue il packing di un array di { colSpan, rowSpan } in una pagina.
 * Ritorna { placed: [{ col, row, colSpan, rowSpan }], emptyCells } oppure null se non ci stanno tutti.
 */
function packPage(items, gridCols = GRID_COLS, gridRows = GRID_ROWS, startRow = 0) {
  const grid = newGrid(gridCols, gridRows);
  // Pre-occupa le righe riservate (es. grafica copertina)
  for (let r = 0; r < startRow; r++)
    for (let c = 0; c < gridCols; c++)
      grid[r][c] = true;

  const placed = [];
  let minRow = startRow;

  for (const item of items) {
    const pos = findFit(grid, item.colSpan, item.rowSpan, gridCols, gridRows, minRow);
    if (!pos) return null; // non ci sta
    markOccupied(grid, pos.col, pos.row, item.colSpan, item.rowSpan);
    minRow = pos.row;
    placed.push({ col: pos.col, row: pos.row, colSpan: item.colSpan, rowSpan: item.rowSpan });
  }

  const occupied = countOccupied(grid) - startRow * gridCols; // celle libere escludendo quelle pre-occupate
  const totalCells = gridCols * (gridRows - startRow);
  return { placed, emptyCells: totalCells - occupied };
}

// ─── 1. Image Fit (optimizeImageSizes) ──────────────────────────────────────

/**
 * Per ogni prodotto, trova l'imgSize più grande (L → M → S) dove il testo
 * ci sta nella zona text. Usa lo scrollHeight reale misurato dalla pre-detection
 * e l'altezza della text zone di ogni variante per decidere.
 *
 * Layout side-by-side (S1, M1, XL): immagine a fianco → sempre L.
 * Layout image-below (S, S2, L, L1, L2 ecc.): confronta scrollHeight con text zone height.
 *
 * @param {object[]}          placements   - array di { layoutId, imgSize, product, ... }
 * @param {object}            config       - config JSON completo
 * @param {Set<number>}       overflowSet  - indici con overflow
 * @param {Map<number,number>} textMeasures - Map<index, scrollHeight in px> dalla pre-detection
 * @param {number}            scale        - pixel per mm (preDetectW / pageWidthMm)
 */
export function optimizeImageSizes(placements, config, overflowSet = new Set(), textMeasures = new Map(), scale = 1, indexToPage = {}) {
  const suggestions = new Map();
  const opts = config?.meta?.optimizations?.imageSize;
  if (!opts?.enabled) return suggestions;

  const sizeOrder = ['L', 'M', 'S'];
  const priceH = config?.meta?.priceSlotHeight ?? 11.026;

  // Auto-detect side-by-side layouts: image beside text in ALL variants,
  // AND text zone width is the same across all variants (pure side-by-side, not hybrid).
  // Hybrid layouts (e.g. L: txt.w=110 in S but 56 in M/L) are excluded and handled via DOM comparison.
  const sideBySize = new Set(opts.sideBySideLayouts || []);
  if (sideBySize.size === 0) {
    for (const l of config?.layouts ?? []) {
      if (l.type === 'table' || l.id.endsWith('img') || l.id.startsWith('bkg')) continue;
      const variants = l.variants || [];
      if (!variants.length) continue;
      const textWidths = variants.map(v => v.zones?.find(z => z.type === 'text')?.w).filter(Boolean);
      const uniformTextWidth = textWidths.length > 0 && new Set(textWidths.map(w => Math.round(w))).size === 1;
      const allSbs = uniformTextWidth && variants.every(v => {
        const img = v.zones?.find(z => z.type === 'image');
        const txt = v.zones?.find(z => z.type === 'text');
        return img && txt && img.x >= txt.w;
      });
      if (allSbs) sideBySize.add(l.id);
    }
  }

  for (let i = 0; i < placements.length; i++) {
    const p = placements[i];
    const layout = findLayout(config, p.layoutId);
    if (!layout) continue;
    if (layout.type === 'table') continue;
    if (p.layoutId.endsWith('img')) continue;
    if (!layout.variants?.length) continue;

    if (sideBySize.has(p.layoutId)) {
      if (p.imgSize !== 'L') {
        suggestions.set(i, { index: i, field: 'imgSize', original: p.imgSize, suggested: 'L', status: 'pending' });
      }
      continue;
    }

    // Hybrid layout (e.g. L): if overflow and variant S has wider text zone, suggest S
    const variantS = layout.variants.find(v => v.imgSize === 'S');
    const variantCurrent = layout.variants.find(v => v.imgSize === p.imgSize) || layout.variants[0];
    const textWS = variantS?.zones?.find(z => z.type === 'text')?.w || 0;
    const textWCurrent = variantCurrent?.zones?.find(z => z.type === 'text')?.w || 0;
    if (overflowSet.has(i) && textWS > textWCurrent && p.imgSize !== 'S') {
      suggestions.set(i, { index: i, field: 'imgSize', original: p.imgSize, suggested: 'S', status: 'pending' });
      continue;
    }

    // bkg2 multi-slot: valuta ogni slot separatamente
    const firstVariant = layout.variants[0];
    if (firstVariant.slots?.length > 1) {
      for (let si = 0; si < firstVariant.slots.length; si++) {
        const slotKey = `${i}:${si}`;
        const scrollH = textMeasures.get(slotKey);
        if (scrollH == null) continue;
        const prod = p.products?.[si];
        const fImgSize = config?.meta?.csvMapping?.['imgSize'] ?? 'Image Size';
        const currentSize = prod?.[fImgSize] || p.imgSize || 'S';
        let bestSize = null;
        for (const size of sizeOrder) {
          const variant = layout.variants.find(v => v.imgSize === size);
          if (!variant) continue;
          const slotZones = variant.slots?.[si]?.zones;
          if (!slotZones) continue;
          const textZone = slotZones.find(z => z.type === 'text');
          if (!textZone) continue;
          const priceZone = slotZones.find(z => z.type === 'price');
          const availableH = (textZone.h + (priceZone ? priceZone.h : 0)) * scale;
          if (scrollH <= Math.ceil(availableH) - 2) {
            bestSize = size;
            break;
          }
        }
        if (!bestSize) bestSize = 'S';
        if (bestSize !== currentSize) {
          suggestions.set(slotKey, { index: slotKey, slotIdx: si, placementIndex: i, field: 'imgSize', original: currentSize, suggested: bestSize, status: 'pending' });
        }
      }
      continue;
    }

    const scrollH = textMeasures.get(i);
    if (scrollH == null) continue;

    // Width of text zone in the variant that was actually measured (current imgSize)
    const measuredVariant = layout.variants.find(v => v.imgSize === p.imgSize) || layout.variants[0];
    const measuredTextW = measuredVariant?.zones?.find(z => z.type === 'text')?.w || 0;

    const hasOverflow = overflowSet.has(i);
    let bestSize = null;
    for (const size of sizeOrder) {
      // Skip current size if DOM detected overflow — it clearly doesn't fit
      if (hasOverflow && size === p.imgSize) continue;
      const variant = layout.variants.find(v => v.imgSize === size);
      if (!variant) continue;
      const textZone = variant.zones?.find(z => z.type === 'text');
      if (!textZone) continue;
      const priceZone = variant.zones?.find(z => z.type === 'price');
      const availableH = (textZone.h + (priceZone ? priceZone.h : 0)) * scale;
      const candTextW = textZone.w;
      const adjustedScrollH = (measuredTextW > 0 && candTextW > measuredTextW)
        ? scrollH * (measuredTextW / candTextW)
        : scrollH;
      if (adjustedScrollH <= Math.ceil(availableH) - 4) {
        bestSize = size;
        break;
      }
    }

    if (!bestSize) bestSize = 'S';

    if (bestSize !== p.imgSize) {
      suggestions.set(i, { index: i, field: 'imgSize', original: p.imgSize, suggested: bestSize, status: 'pending' });
    }
  }

  return suggestions;
}

// ─── 2. Space Fill (fillEmptySpace) ─────────────────────────────────────────

/**
 * Per ogni pagina, trova la combinazione di layout che minimizza le celle vuote.
 * Non inserisce nuovi prodotti, ottimizza quelli esistenti.
 *
 * @param {object[][]} pages       - array di pagine, ogni pagina è un array di placement posizionati
 * @param {object[]}   placements  - placements originali (per leggere i prodotti)
 * @param {object}     config      - config JSON completo (con meta.optimizations.fillGaps)
 * @returns {Map<number, object>}  - Map<originalIndex, { index, field, original, suggested, status }>
 */
export function fillEmptySpace(pages, placements, config, lastPageRows = GRID_ROWS, firstPageStartRow = 0) {
  const suggestions = new Map();
  const opts = config?.meta?.optimizations?.fillGaps;
  if (!opts?.enabled) return suggestions;

  const csvMapping = config?.meta?.csvMapping || {};
  const layoutGroups = opts.layoutGroups || [];
  const excludeLayouts = new Set(opts.excludeLayouts || []);
  const priority = opts.priority || ['minGaps', 'minSizeDist', 'maxImageArea', 'minChanges'];

  // Candidati standard: da layoutGroups
  const allCandidateIds = [...new Set(layoutGroups.flat())].filter(id => !excludeLayouts.has(id));
  // Candidati img-only: tutti i layout che finiscono per "img"
  const allImgCandidateIds = (config?.layouts ?? [])
    .filter(l => l.id.endsWith('img') && !excludeLayouts.has(l.id))
    .map(l => l.id);

  // Build img groups that mirror standard groups by row height
  const imgGroups = layoutGroups.map(group => {
    const rowSet = new Set(group.map(id => findLayout(config, id)?.rows).filter(Boolean));
    return allImgCandidateIds.filter(id => {
      const imgLayout = findLayout(config, id);
      return imgLayout && rowSet.has(imgLayout.rows);
    });
  });


  for (let pageIdx = 0; pageIdx < pages.length; pageIdx++) {
    const page = pages[pageIdx];
    if (!page?.length) continue;

    const modifiable = [];
    const fixed = [];

    for (let pi = 0; pi < page.length; pi++) {
      const placed = page[pi];
      const layout = findLayout(config, placed.layoutId);
      if (!layout) { fixed.push(placed); continue; }

      if (layout.type === 'table' || placed.layoutId.startsWith('bkg') || excludeLayouts.has(placed.layoutId)) {
        fixed.push(placed);
        continue;
      }

      const isImg = placed.layoutId.endsWith('img');
      const origArea = layout.cols * layout.rows;

      if (isImg) {
        const myImgGroup = imgGroups.find(g => g.includes(placed.layoutId));
        const imgGroupIds = myImgGroup ? myImgGroup.filter(id => !excludeLayouts.has(id)) : [];

        function buildImgCompatible(candidateIds) {
          return candidateIds.map(id => {
            const candLayout = findLayout(config, id);
            if (!candLayout) return null;
            const candArea = candLayout.cols * candLayout.rows;
            return { layoutId: id, imgSize: placed.imgSize || 'S', cols: candLayout.cols, rows: candLayout.rows, sizeDist: Math.abs(candArea - origArea) };
          }).filter(Boolean);
        }

        const groupCompatible = buildImgCompatible(imgGroupIds);
        const allCompatible = buildImgCompatible(allImgCandidateIds);

        if (allCompatible.length > 0) {
          modifiable.push({ pageIdx: pi, originalIndex: placed.originalIndex, placement: placed, compatible: groupCompatible.length > 0 ? groupCompatible : allCompatible, allCompatible });
        } else {
          fixed.push(placed);
        }
        continue;
      }

      // Standard: deve appartenere ad almeno un gruppo
      const myGroup = layoutGroups.find(g => g.includes(placed.layoutId));
      if (!myGroup) { fixed.push(placed); continue; }

      const product = placed.product || placements[placed.originalIndex]?.product;
      const textFields = layout.variants?.[0]?.zones?.find(z => z.type === 'text')?.fields;
      const currentImgSize = placed.imgSize || 'S';

      function findCompatible(candidateIds) {
        const result = [];
        for (const layoutId of candidateIds) {
          const candLayout = findLayout(config, layoutId);
          if (!candLayout?.variants?.length) continue;
          const variant = candLayout.variants.find(v => v.imgSize === currentImgSize) || candLayout.variants[0];
          const avgCh = variant?.avgCh || 65;
          const maxChars = avgCh * (variant?.maxRows || 0);
          const textLen = textFields ? charCountWithBreaks(product, textFields, csvMapping, avgCh) : Infinity;
          if (textLen <= maxChars) {
            const candArea = candLayout.cols * candLayout.rows;
            result.push({
              layoutId, imgSize: currentImgSize,
              cols: candLayout.cols, rows: candLayout.rows,
              sizeDist: Math.abs(candArea - origArea),
            });
          }
        }
        return result;
      }

      const groupCandidateIds = myGroup.filter(id => !excludeLayouts.has(id));
      const groupCompatible = findCompatible(groupCandidateIds);
      const allCompatible = findCompatible(allCandidateIds);

      // Always include current layout as a candidate (user already chose it)
      const currentEntry = { layoutId: placed.layoutId, imgSize: currentImgSize, cols: layout.cols, rows: layout.rows, sizeDist: 0 };
      if (!groupCompatible.find(c => c.layoutId === placed.layoutId)) {
        groupCompatible.unshift(currentEntry);
      }
      if (!allCompatible.find(c => c.layoutId === placed.layoutId)) {
        allCompatible.unshift(currentEntry);
      }

      if (allCompatible.length > 0) {
        modifiable.push({ pageIdx: pi, originalIndex: placed.originalIndex, placement: placed, compatible: groupCompatible.length > 0 ? groupCompatible : allCompatible, allCompatible });
      } else {
        fixed.push(placed);
      }
    }

    // Se nessun placement è modificabile, skip
    if (modifiable.length === 0) {
      continue;
    }

    const isLastPage = pageIdx === pages.length - 1;
    const isFirstPage = pageIdx === 0;
    const startRow = isFirstPage ? firstPageStartRow : 0;
    const pageRows = isLastPage ? lastPageRows : GRID_ROWS;
    const totalCells = GRID_COLS * (pageRows - startRow);
    const currentOccupied = page.reduce((s, p) => s + p.colSpan * p.rowSpan, 0);

    if (currentOccupied >= totalCells) {
      continue;
    }


    // Costruisci gli items base della pagina (per packing)
    function buildItems(choices) {
      // choices: Map<pageIdx, { layoutId, imgSize, cols, rows }>
      const items = [];
      for (let pi = 0; pi < page.length; pi++) {
        const choice = choices.get(pi);
        if (choice) {
          const candLayout = findLayout(config, choice.layoutId);
          items.push({
            colSpan: candLayout?.cols || choice.cols,
            rowSpan: candLayout?.rows || choice.rows,
            layoutId: choice.layoutId,
            imgSize: choice.imgSize,
          });
        } else {
          const p = page[pi];
          items.push({
            colSpan: p.colSpan,
            rowSpan: p.rowSpan,
            layoutId: p.layoutId,
            imgSize: p.imgSize,
          });
        }
      }
      return items;
    }

    function scoreItems(items) {
      const result = packPage(items, GRID_COLS, pageRows, startRow);
      if (!result) return null;
      const emptyCells = result.emptyCells;
      const changes = countChanges(page, items, modifiable);
      const imageArea = totalImageArea(items, config);
      const sizeDist = totalSizeDist(page, items, modifiable);
      return calcScore(emptyCells, imageArea, changes, sizeDist, priority);
    }

    // ── Two-step search: same-group first, cross-group if gaps remain ──
    function runSearch(candidateKey) {
      const maxCombos = 50000;
      const comboCount = modifiable.reduce((acc, m) => acc * m[candidateKey].length, 1);

      let bestCombo = null;
      let bestScore = null;

      if (comboCount <= maxCombos) {
        const combos = cartesian(modifiable.map(m => m[candidateKey]));
        for (const combo of combos) {
          const choices = new Map();
          combo.forEach((choice, ci) => choices.set(modifiable[ci].pageIdx, choice));
          const items = buildItems(choices);
          const score = scoreItems(items);
          if (score && (!bestScore || compareScore(score, bestScore) < 0)) {
            bestScore = score;
            bestCombo = items;
          }
        }
      } else {
        const currentChoices = new Map();
        for (const mod of modifiable) {
          currentChoices.set(mod.pageIdx, {
            layoutId: mod.placement.layoutId,
            imgSize: mod.placement.imgSize,
            cols: mod.placement.colSpan,
            rows: mod.placement.rowSpan,
          });
        }

        let improved = true;
        let iterations = 0;
        while (improved && iterations < 3) {
          improved = false;
          iterations++;
          for (const mod of modifiable) {
            const saved = currentChoices.get(mod.pageIdx);
            let localBestScore = scoreItems(buildItems(currentChoices));
            let localBestChoice = saved;

            for (const option of mod[candidateKey]) {
              currentChoices.set(mod.pageIdx, option);
              const items = buildItems(currentChoices);
              const score = scoreItems(items);
              if (score && (!localBestScore || compareScore(score, localBestScore) < 0)) {
                localBestScore = score;
                localBestChoice = option;
              }
            }

            currentChoices.set(mod.pageIdx, localBestChoice);
            if (localBestChoice !== saved) improved = true;
          }
        }

        bestCombo = buildItems(currentChoices);
        bestScore = scoreItems(bestCombo);
      }

      return { bestCombo, bestScore };
    }

    // Step 1: same-group candidates
    let { bestCombo, bestScore } = runSearch('compatible');

    // Step 2: if gaps remain, retry with cross-group candidates
    const gapIdx = priority.indexOf('minGaps');
    const hasAllCompatible = modifiable.some(m => m.allCompatible && m.allCompatible.length > m.compatible.length);
    const gapCount = gapIdx >= 0 && bestScore ? bestScore[gapIdx] : 0;
    if (hasAllCompatible && gapCount > 0) {
      // Swap to allCompatible for modifiable entries that have it
      for (const mod of modifiable) {
        if (mod.allCompatible) mod.compatible = mod.allCompatible;
      }
      const cross = runSearch('compatible');
      if (cross.bestScore && (!bestScore || compareScore(cross.bestScore, bestScore) < 0)) {
        bestCombo = cross.bestCombo;
        bestScore = cross.bestScore;
      }
    }

    // Confronta con lo score attuale — genera suggerimenti solo se il best è strettamente migliore
    const currentItems = page.map(p => ({ colSpan: p.colSpan, rowSpan: p.rowSpan, layoutId: p.layoutId, imgSize: p.imgSize }));
    const currentScore = scoreItems(currentItems);

    const currentDesc = page.map(p => `${p.layoutId}/${p.imgSize}(${p.colSpan}×${p.rowSpan})`);
    const bestDesc = bestCombo?.map(i => `${i.layoutId}/${i.imgSize}(${i.colSpan}×${i.rowSpan})`);

    const isBetter = bestScore && currentScore && compareScore(bestScore, currentScore) < 0;
    if (!bestScore) {
      for (const mod of modifiable) {
      }
    }

    if (bestCombo && isBetter) {
      for (const mod of modifiable) {
        const bestItem = bestCombo[mod.pageIdx];
        if (!bestItem) continue;

        const currentLayoutId = mod.placement.layoutId;
        const currentImgSize = mod.placement.imgSize;

        if (bestItem.layoutId !== currentLayoutId) {
          suggestions.set(mod.originalIndex, {
            index: mod.originalIndex,
            field: 'layout',
            original: currentLayoutId,
            suggested: bestItem.layoutId,
            status: 'pending',
          });
        }
      }
    }
  }

  return suggestions;
}

// ─── Helpers per Space Fill ──────────────────────────────────────────────────

/**
 * Prodotto cartesiano di un array di array.
 * cartesian([[a,b], [c,d]]) → [[a,c],[a,d],[b,c],[b,d]]
 */
function cartesian(arrays) {
  if (arrays.length === 0) return [[]];
  return arrays.reduce(
    (acc, arr) => acc.flatMap(combo => arr.map(item => [...combo, item])),
    [[]]
  );
}

/**
 * Conta quanti placement cambiano rispetto all'attuale.
 */
function countChanges(page, items, modifiable) {
  let changes = 0;
  for (const mod of modifiable) {
    const current = page[mod.pageIdx];
    const proposed = items[mod.pageIdx];
    if (current.layoutId !== proposed.layoutId || current.imgSize !== proposed.imgSize) {
      changes++;
    }
  }
  return changes;
}

/**
 * Calcola la distanza totale di dimensione tra layout originali e proposti.
 * Penalizza cambi drastici (es. L→S più penalizzato di L→M).
 */
function totalSizeDist(page, items, modifiable) {
  let dist = 0;
  for (const mod of modifiable) {
    const orig = page[mod.pageIdx];
    const proposed = items[mod.pageIdx];
    const origArea = orig.colSpan * orig.rowSpan;
    const propArea = proposed.colSpan * proposed.rowSpan;
    dist += Math.abs(propArea - origArea);
  }
  return dist;
}

/**
 * Calcola l'area immagine totale degli items (proxy: colSpan × rowSpan più grande = immagine più grande).
 * Per il ranking "maxImageArea" si vuole massimizzare, quindi usiamo il negativo.
 */
function totalImageArea(items, config) {
  let area = 0;
  for (const item of items) {
    const layout = findLayout(config, item.layoutId);
    if (!layout?.variants) continue;
    const variant = layout.variants.find(v => v.imgSize === item.imgSize);
    const imgZone = variant?.zones?.find(z => z.type === 'image');
    if (imgZone) {
      area += imgZone.w * imgZone.h;
    }
  }
  return area;
}

/**
 * Calcola un vettore di score in base alla priorità configurata.
 */
function calcScore(emptyCells, imageArea, changes, sizeDist, priority) {
  const scores = {};
  scores.minGaps = emptyCells;
  scores.minSizeDist = sizeDist;       // penalizza cambi drastici di dimensione
  scores.maxImageArea = -imageArea;     // negativo: più area = meglio
  scores.minChanges = changes;
  return priority.map(p => scores[p] ?? 0);
}

/**
 * Confronta due vettori di score. Ritorna negativo se a è meglio, positivo se b è meglio.
 */
function compareScore(a, b) {
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return a[i] - b[i];
  }
  return 0;
}
